import { auth } from '@/services/firebase/config';
import { AIAnalysisResult } from '@/types/ai';

// ── T108/T109: Token costs must mirror the backend ACTION_TOKEN_COSTS ──
export const ACTION_TOKEN_COSTS: Record<string, number> = {
  transcribe: 10,
  stt: 5,
  format: 5,
  extract: 5,
  suggest: 5,
  ocr: 3,
  extract_json: 3,
  general: 5,
};

export function getActionTokenCost(action: string): number {
  return ACTION_TOKEN_COSTS[action] ?? ACTION_TOKEN_COSTS['general'];
}

export const GeminiClient = {
  /**
   * T109: Pre-check token balance before calling the CF.
   * Returns { balance, costs } or throws if insufficient.
   */
  async checkTokenBalance(actionType: string): Promise<{ balance: number; costs: Record<string, number> }> {
    const user = auth.currentUser;
    if (!user) throw new Error('Usuario nao logado.');

    const token = await user.getIdToken(true);
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'dratento';
    const endpoint = `https://us-central1-${projectId}.cloudfunctions.net/gemini/token-balance`;

    const res = await fetch(endpoint, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!res.ok) {
      throw new Error('Erro ao verificar saldo de tokens.');
    }

    const data = await res.json();
    const cost = getActionTokenCost(actionType);

    // Unlimited accounts bypass balance check
    if (data.unlimited) {
      return { balance: 999999, costs: data.costs };
    }

    if (data.balance < cost) {
      throw new Error(
        `Saldo insuficiente. Necessario: ${cost} tokens. Saldo atual: ${data.balance} tokens.`
      );
    }

    return { balance: data.balance, costs: data.costs };
  },

  /**
   * Sends audio/blob to the Gemini proxy function.
   * For 'transcribe' action, returns structured clinical data alongside markdown.
   */
  async analyzeContent(file: Blob, actionType: string, template: string, interlocutors: number): Promise<AIAnalysisResult> {
    const user = auth.currentUser;
    if (!user) throw new Error('Usuario nao logado.');

    const token = await user.getIdToken(true);

    const base64data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
    });

    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'dratento';
    const endpoint = `https://us-central1-${projectId}.cloudfunctions.net/gemini/analyze`;
    const textContext = `Especialidade/Template: ${template}. Quantidade de falantes (interlocutores): ${interlocutors}.`;

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: actionType,
        inputType: 'audio',
        text: textContext,
        audioBase64: base64data,
        audioMimeType: file.type || 'audio/webm',
        interlocutors,
      }),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      if (res.status === 402) {
        throw new Error((errorData as any).error || 'Saldo de tokens insuficiente.');
      }
      throw new Error((errorData as any).error || 'Erro na requisicao para analise.');
    }

    const json = await res.json();
    return {
      markdown: json.markdown || json.text || '',
      structured: json.structured ?? null,
      tokensConsumed: json.tokensConsumed ?? 0,
      model: json.model,
    };
  },

  /**
   * Multimodal analysis: sends audio + images + PDFs + text in one request.
   * The backend builds a single Gemini call with all attachments as inlineData,
   * so the model considers everything together when producing the structured
   * clinical record.
   */
  async analyzeMultimodal(params: {
    audio: Blob | null;
    images: File[];
    pdfs: File[];
    text?: string;
    action: string;
    template: string;
    interlocutors: number;
    /**
     * Linha do tempo de quem falou (spec 016, fase 2). Com ela, o "há só 1
     * falante" do servidor sai (interlocutores 0) e a linha vai no texto.
     */
    falantes?: string | null;
    /** Permite cancelar a chamada de fetch. */
    signal?: AbortSignal;
  }): Promise<AIAnalysisResult> {
    const user = auth.currentUser;
    if (!user) throw new Error('Usuario nao logado.');

    const token = await user.getIdToken(true);

    // Helper: Blob -> base64 (no data: prefix)
    const toBase64 = (blob: Blob): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
      });

    // Build attachments array (images + pdfs as inlineData parts)
    const attachments: Array<{ data: string; mimeType: string; name?: string }> = [];

    for (const img of params.images) {
      attachments.push({
        data: await toBase64(img),
        mimeType: img.type || 'image/jpeg',
        name: img.name,
      });
    }
    for (const pdf of params.pdfs) {
      attachments.push({
        data: await toBase64(pdf),
        mimeType: pdf.type || 'application/pdf',
        name: pdf.name,
      });
    }

    // Audio is sent as the dedicated audio field (keeps backward compat with the existing prompt path)
    let audioBase64: string | undefined;
    let audioMimeType: string | undefined;
    if (params.audio) {
      audioBase64 = await toBase64(params.audio);
      audioMimeType = params.audio.type || 'audio/webm';
    }

    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'dratento';
    const endpoint = `https://us-central1-${projectId}.cloudfunctions.net/gemini/analyze`;

    const interlocutors = params.falantes ? 0 : params.interlocutors;
    const textContext = [
      `Especialidade/Template: ${params.template}.`,
      params.falantes ? `\n${params.falantes}\n` : interlocutors > 0 ? `Quantidade de falantes (interlocutores): ${interlocutors}.` : '',
      params.text ? `\nObservações do médico:\n${params.text}` : '',
    ]
      .filter(Boolean)
      .join(' ');

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: params.action,
        inputType: 'multimodal',
        text: textContext,
        audioBase64,
        audioMimeType,
        attachments,
        interlocutors,
      }),
      signal: params.signal,
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      const backendMsg = (errorData as { error?: string }).error;
      if (res.status === 402) {
        throw new Error(backendMsg || 'Saldo de tokens insuficiente.');
      }
      if (res.status === 413) {
        throw new Error('Arquivos grandes demais. Reduza o tamanho ou a quantidade de anexos.');
      }
      if (res.status === 429) {
        throw new Error(backendMsg || 'Limite de uso da IA atingido. Aguarde alguns minutos.');
      }
      if (res.status === 503) {
        throw new Error(backendMsg || 'Servidor da IA sobrecarregado. Tente novamente em instantes.');
      }
      if (res.status === 504) {
        throw new Error(backendMsg || 'Tempo esgotado ao chamar a IA. Tente novamente.');
      }
      if (res.status === 502) {
        throw new Error(backendMsg || 'Falha de rede ao contatar a IA. Tente novamente.');
      }
      throw new Error(backendMsg || `Erro na análise multimodal (HTTP ${res.status}).`);
    }

    const json = await res.json();
    return {
      markdown: json.markdown || json.text || '',
      structured: json.structured ?? null,
      tokensConsumed: json.tokensConsumed ?? 0,
      model: json.model,
    };
  },

  /**
   * Sends plain text to the Gemini proxy function.
   */
  async analyzeText(text: string, actionType: string): Promise<AIAnalysisResult> {
    const user = auth.currentUser;
    if (!user) throw new Error('Usuario nao logado.');

    const token = await user.getIdToken(true);
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'dratento';
    const endpoint = `https://us-central1-${projectId}.cloudfunctions.net/gemini/analyze`;

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action: actionType, text }),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      if (res.status === 402) {
        throw new Error((errorData as any).error || 'Saldo de tokens insuficiente.');
      }
      throw new Error((errorData as any).error || 'Erro na requisicao para analise.');
    }

    const json = await res.json();
    return {
      markdown: json.markdown || json.text || '',
      structured: json.structured ?? null,
      tokensConsumed: json.tokensConsumed ?? 0,
      model: json.model,
    };
  },

  /**
   * Etapa 1 (fluxo de 2 passos): transcreve o áudio em texto cru (action 'stt'),
   * sem estruturar. O modelo é resolvido no backend (Groq Whisper ou Gemini).
   */
  async transcribeAudio(file: Blob, interlocutors: number): Promise<{ text: string; model?: string }> {
    const user = auth.currentUser;
    if (!user) throw new Error('Usuario nao logado.');
    const token = await user.getIdToken(true);

    const base64data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
    });

    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'dratento';
    const endpoint = `https://us-central1-${projectId}.cloudfunctions.net/gemini/analyze`;

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'stt',
        audioBase64: base64data,
        audioMimeType: file.type || 'audio/webm',
        interlocutors,
      }),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error((errorData as { error?: string }).error || 'Erro na transcrição.');
    }
    const json = await res.json();
    return { text: json.text || '', model: json.model };
  },

  /**
   * Etapa 2 (fluxo de 2 passos): formata um texto (transcrição revisada pelo
   * médico) no prontuário estruturado (action 'format'). Retorna markdown + structured.
   */
  async formatText(
    text: string,
    template: string,
    interlocutors: number,
    /** Contexto adicional do MESMO atendimento (observações do médico,
     *  documentos manuais já preenchidos…). Entra rotulado como REFERÊNCIA
     *  — o modelo usa pra coerência, não copia literal pra evolução. */
    extraContext?: string,
  ): Promise<AIAnalysisResult> {
    const user = auth.currentUser;
    if (!user) throw new Error('Usuario nao logado.');
    const token = await user.getIdToken(true);

    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'dratento';
    const endpoint = `https://us-central1-${projectId}.cloudfunctions.net/gemini/analyze`;
    // 0 = não afirma quantos falam (o texto do pedido explica). Com 1, o
    // servidor diz "só o médico fala" — e a conversa vira toda relato.
    const context = interlocutors > 0
      ? `Especialidade/Template: ${template}. Quantidade de falantes (interlocutores): ${interlocutors}.`
      : `Especialidade/Template: ${template}.`;

    const body = extraContext && extraContext.trim()
      ? `${context}\n\n${text}\n\n---\n### CONTEXTO ADICIONAL DESTE ATENDIMENTO (referência — NÃO copie literalmente; use só para coerência e completar dados da evolução):\n${extraContext.trim()}`
      : `${context}\n\n${text}`;

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'format',
        text: body,
        interlocutors,
      }),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error((errorData as { error?: string }).error || 'Erro na formatação.');
    }
    const json = await res.json();
    return {
      markdown: json.markdown || json.text || '',
      structured: json.structured ?? null,
      tokensConsumed: json.tokensConsumed ?? 0,
      model: json.model,
    };
  },
};
